import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import api from '../api/api';
import SupportResourcesPanel from './SupportResourcesPanel';

const ISSUE_TYPE_OPTIONS = [
  { value: 'system_bug', label: 'Something is broken (system bug)' },
  { value: 'how_to', label: 'I need help using a feature' },
  { value: 'class_content', label: 'Class or content issue' },
  { value: 'account_support', label: 'Account or profile help' },
  { value: 'billing_support', label: 'Billing or payment issue' },
  { value: 'general_support', label: 'General question or feedback' },
];

const FEATURE_AREA_OPTIONS = [
  { value: 'Login & Authentication', label: 'Login & Authentication' },
  { value: 'Dashboard overview', label: 'Dashboard overview' },
  { value: 'Live classes & sessions', label: 'Live classes & sessions' },
  { value: 'Homework & submissions', label: 'Homework & submissions' },
  { value: 'Recordings & replays', label: 'Recordings & replays' },
  { value: 'Messaging & announcements', label: 'Messaging & announcements' },
  { value: 'Resources & downloads', label: 'Resources & downloads' },
  { value: 'Payments & billing', label: 'Payments & billing' },
  { value: 'Profile & account settings', label: 'Profile & account settings' },
  { value: 'Other / not sure', label: 'Other / not sure' },
];

const IMPACT_LEVEL_OPTIONS = [
  { value: 'blocker', label: 'Blocker - I cannot continue my work' },
  { value: 'high', label: 'High - major disruption' },
  { value: 'medium', label: 'Medium - inconvenient but manageable' },
  { value: 'low', label: 'Low - minor or cosmetic' },
];

const FREQUENCY_OPTIONS = [
  { value: 'always', label: 'Every time I try' },
  { value: 'intermittent', label: 'Sometimes / intermittent' },
  { value: 'once', label: 'It happened once' },
];

const DEVICE_OPTIONS = [
  { value: '', label: 'Select device' },
  { value: 'Laptop or desktop', label: 'Laptop or desktop' },
  { value: 'Tablet', label: 'Tablet' },
  { value: 'Mobile phone', label: 'Mobile phone' },
  { value: 'Other', label: 'Other' },
];

const CONNECTION_OPTIONS = [
  { value: '', label: 'Select connection type' },
  { value: 'Wi-Fi', label: 'Wi-Fi' },
  { value: 'Mobile data', label: 'Mobile data' },
  { value: 'Wired / ethernet', label: 'Wired / ethernet' },
  { value: 'Not sure', label: 'Not sure' },
];

const SELF_HELP_OPTIONS = [
  { value: 'refreshed_page', label: 'Reloaded or refreshed the page' },
  { value: 'different_device', label: 'Tried a different device or browser' },
  { value: 'checked_connection', label: 'Checked my internet connection' },
  { value: 'checked_status', label: 'Looked at announcements or status updates' },
  { value: 'reviewed_help', label: 'Reviewed help articles or guides' },
  { value: 'other', label: 'Something else' },
];

const CATEGORY_OPTIONS = ['General', 'Technical', 'Classes', 'Billing', 'Account'];
const TEXT_COLOR = 'rgb(0, 21, 74)';
const CARD_RADIUS = 3;

const makeInitialForm = () => ({
  subject: '',
  category: 'General',
  message: '',
  issueType: 'system_bug',
  featureArea: '',
  impactLevel: 'medium',
  frequency: 'intermittent',
  blocking: 'no',
  stepsToReproduce: '',
  expectedBehavior: '',
  actualBehavior: '',
  environmentDevice: '',
  environmentOperatingSystem: '',
  environmentBrowser: '',
  environmentConnection: '',
  selfHelpTried: [],
  selfHelpOther: '',
});

const formatOptionLabel = (options, value) => {
  const match = options.find((opt) => opt.value === value);
  return match ? match.label : value;
};

const formatDateTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
};

const SupportTicketForm = ({ userRole = 'student' }) => {
  const [form, setForm] = useState(() => makeInitialForm());
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);

  const isBugTicket = form.issueType === 'system_bug';
  const hasOtherSelfHelp = form.selfHelpTried.includes('other');

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };
  const toggleSelfHelp = (value) => {
    setForm((prev) => {
      const exists = prev.selfHelpTried.includes(value);
      const next = exists
        ? prev.selfHelpTried.filter((item) => item !== value)
        : [...prev.selfHelpTried, value];
      return { ...prev, selfHelpTried: next };
    });
    setError('');
    setSuccess('');
  };

  const buildSelfHelpPayload = useCallback(() => {
    const items = [];
    form.selfHelpTried.forEach((value) => {
      if (value === 'other') {
        const note = form.selfHelpOther.trim();
        if (note) items.push(note);
      } else {
        const option = SELF_HELP_OPTIONS.find((opt) => opt.value === value);
        if (option) items.push(option.label);
      }
    });
    return items;
  }, [form.selfHelpOther, form.selfHelpTried]);

  const buildStructuredPayload = useCallback(() => {
    const structured = {
      issueType: form.issueType,
      featureArea: form.featureArea || 'General',
      impactLevel: form.impactLevel,
      frequency: form.frequency,
      isBlocking: form.blocking === 'yes',
    };

    const steps = form.stepsToReproduce.trim();
    const expected = form.expectedBehavior.trim();
    const actual = form.actualBehavior.trim();
    if (steps) structured.stepsToReproduce = steps;
    if (expected) structured.expectedBehavior = expected;
    if (actual) structured.actualBehavior = actual;

    const environment = {
      device: form.environmentDevice,
      operatingSystem: form.environmentOperatingSystem.trim(),
      browser: form.environmentBrowser.trim(),
      connection: form.environmentConnection,
    };
    Object.keys(environment).forEach((key) => {
      if (!environment[key]) delete environment[key];
    });
    if (Object.keys(environment).length) {
      structured.environment = environment;
    }

    const selfHelp = buildSelfHelpPayload();
    if (selfHelp.length) {
      structured.selfHelpTried = selfHelp;
    }

    return structured;
  }, [buildSelfHelpPayload, form.actualBehavior, form.environmentBrowser, form.environmentConnection, form.environmentDevice, form.environmentOperatingSystem, form.expectedBehavior, form.featureArea, form.frequency, form.impactLevel, form.issueType, form.stepsToReproduce, form.blocking]);

  const loadTickets = useCallback(async () => {
    setLoadingTickets(true);
    try {
      const res = await api.get('/support');
      setTickets(res?.data?.items || []);
    } catch (_error) {
      // ignore silently
    } finally {
      setLoadingTickets(false);
    }
  }, []);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!form.subject.trim() || !form.message.trim()) {
      setError('Please provide both a subject and message.');
      return;
    }
    if (!form.featureArea) {
      setError('Select where the issue is happening.');
      return;
    }
    if (hasOtherSelfHelp && !form.selfHelpOther.trim()) {
      setError('Tell us what else you tried.');
      return;
    }
    if (isBugTicket) {
      if (!form.stepsToReproduce.trim() || !form.expectedBehavior.trim() || !form.actualBehavior.trim()) {
        setError('For system bugs include the steps, expected behaviour, and what actually happened.');
        return;
      }
    }

    setSubmitting(true);
    try {
      await api.post('/support', {
        subject: form.subject.trim(),
        message: form.message.trim(),
        category: form.category,
        structured: buildStructuredPayload(),
      });
      setSuccess('Thanks! Your ticket has been submitted. Our team will get back to you.');
      setForm(makeInitialForm());
      loadTickets();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to submit support ticket.');
    } finally {
      setSubmitting(false);
    }
  };

  const isEducatorRole = userRole === 'educator' || userRole === 'tutor';
  const supportTeamLabel = isEducatorRole ? 'educator support team' : 'student success team';

  const ticketsOrdered = useMemo(
    () =>
      tickets
        .slice()
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [tickets]
  );

  return (
    <Box sx={{ color: TEXT_COLOR }}>
      <Stack spacing={3}>
        <Card elevation={1} sx={{ borderRadius: 3 }}>
          <CardHeader
            avatar={<SupportAgentRoundedIcon color="primary" />}
            title={<Typography variant="h6" fontWeight={700} sx={{ color: TEXT_COLOR }}>Support</Typography>}
            subheader={`Share what is happening so our ${supportTeamLabel} can get you back on track quickly.`}
            subheaderTypographyProps={{ sx: { color: TEXT_COLOR } }}
          />
          <Divider />
          <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}
            <Box component="form" onSubmit={submit} noValidate sx={{ maxWidth: 1100, mr: { md: 2 } }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Subject"
                    name="subject"
                    value={form.subject}
                    onChange={onChange}
                    placeholder="Brief summary"
                    fullWidth
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Category"
                    name="category"
                    value={form.category}
                    onChange={onChange}
                    fullWidth
                  >
                    {CATEGORY_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Issue type"
                    name="issueType"
                    value={form.issueType}
                    onChange={onChange}
                    fullWidth
                    helperText="Picking the closest match helps prioritise your ticket."
                  >
                    {ISSUE_TYPE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel id="featureArea-label">Where is this happening?</InputLabel>
                    <Select
                      labelId="featureArea-label"
                      id="featureArea"
                      name="featureArea"
                      value={form.featureArea}
                      label="Where is this happening?"
                      onChange={onChange}
                    >
                      <MenuItem value="">
                        <em>Select an area</em>
                      </MenuItem>
                      {FEATURE_AREA_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    label="Impact"
                    name="impactLevel"
                    value={form.impactLevel}
                    onChange={onChange}
                    fullWidth
                  >
                    {IMPACT_LEVEL_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    label="Frequency"
                    name="frequency"
                    value={form.frequency}
                    onChange={onChange}
                    fullWidth
                  >
                    {FREQUENCY_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl component="fieldset">
                    <FormLabel sx={{ color: TEXT_COLOR }}>Is this blocking you?</FormLabel>
                    <RadioGroup row name="blocking" value={form.blocking} onChange={onChange}>
                      <FormControlLabel value="yes" control={<Radio />} label="Yes, I am blocked" />
                      <FormControlLabel value="no" control={<Radio />} label="No, I can keep working" />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                {isBugTicket && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'grey.50',
                        p: 2,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ color: TEXT_COLOR, mb: 2 }}>
                        Bug details
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            label="Steps to reproduce"
                            name="stepsToReproduce"
                            value={form.stepsToReproduce}
                            onChange={onChange}
                            placeholder="List each step so we can recreate the issue."
                            multiline
                            minRows={2}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="What should have happened?"
                            name="expectedBehavior"
                            value={form.expectedBehavior}
                            onChange={onChange}
                            placeholder="Describe the expected behaviour."
                            multiline
                            minRows={2}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="What actually happened?"
                            name="actualBehavior"
                            value={form.actualBehavior}
                            onChange={onChange}
                            placeholder="Describe what you experienced instead."
                            multiline
                            minRows={2}
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Box
                    sx={{
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'grey.50',
                      p: 2,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ color: TEXT_COLOR, mb: 2 }}>
                      Device & environment
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          select
                          label="Device"
                          name="environmentDevice"
                          value={form.environmentDevice}
                          onChange={onChange}
                          fullWidth
                        >
                          {DEVICE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value || 'device-empty'} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          select
                          label="Connection type"
                          name="environmentConnection"
                          value={form.environmentConnection}
                          onChange={onChange}
                          fullWidth
                        >
                          {CONNECTION_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value || 'connection-empty'} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Operating system"
                          name="environmentOperatingSystem"
                          value={form.environmentOperatingSystem}
                          onChange={onChange}
                          placeholder="e.g. Windows 11, iOS 17"
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Browser or app version"
                          name="environmentBrowser"
                          value={form.environmentBrowser}
                          onChange={onChange}
                          placeholder="e.g. Chrome 125, Safari"
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                    <FormHelperText sx={{ color: TEXT_COLOR, mt: 1 }}>
                      Device details help us recreate your environment.
                    </FormHelperText>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'grey.50',
                      p: 2,
                    }}
                  >
                    <FormControl component="fieldset" sx={{ width: '100%' }}>
                      <FormLabel sx={{ color: TEXT_COLOR }}>What have you already tried?</FormLabel>
                      <FormGroup sx={{ gap: 1 }}>
                        {SELF_HELP_OPTIONS.map((opt) => (
                          <FormControlLabel
                            key={opt.value}
                            control={
                              <Checkbox
                                checked={form.selfHelpTried.includes(opt.value)}
                                onChange={() => toggleSelfHelp(opt.value)}
                              />
                            }
                            label={opt.label}
                          />
                        ))}
                      </FormGroup>
                      {hasOtherSelfHelp && (
                        <TextField
                          sx={{ mt: 2, maxWidth: 420 }}
                          label="What else did you try?"
                          name="selfHelpOther"
                          value={form.selfHelpOther}
                          onChange={onChange}
                          fullWidth
                        />
                      )}
                    </FormControl>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Anything else we should know?"
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    placeholder="Add any context, links, or times we should check."
                    multiline
                    minRows={4}
                    fullWidth
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
                    <Button
                      type="submit"
                      variant="contained"
 startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SendRoundedIcon />}
                      disabled={submitting}
                    >
 {submitting ? 'Submitting...' : 'Submit ticket'}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>

        <Card elevation={1} sx={{ borderRadius: 3 }}>
          <CardHeader
            title={<Typography variant="h6" sx={{ color: TEXT_COLOR }}>Your recent tickets</Typography>}
            subheader="We keep your latest submissions here for quick reference."
            subheaderTypographyProps={{ sx: { color: TEXT_COLOR } }}
          />
          <Divider />
          <CardContent>
            {loadingTickets ? (
              <Stack alignItems="center" spacing={2} py={4}>
                <CircularProgress size={28} />
                <Typography variant="body2" sx={{ color: TEXT_COLOR }}>
                  Loading tickets...
                </Typography>
              </Stack>
 ) : ticketsOrdered.length === 0 ? (
              <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                No tickets yet. Reach out whenever you need help.
              </Alert>
 ) : (
              <Stack spacing={2}>
                {ticketsOrdered.map((ticket) => {
                  const metaChips = [];
                  const issueLabel = formatOptionLabel(ISSUE_TYPE_OPTIONS, ticket.structured?.issueType);
                  const impactLabel = formatOptionLabel(IMPACT_LEVEL_OPTIONS, ticket.structured?.impactLevel);
                  const frequencyLabel = formatOptionLabel(FREQUENCY_OPTIONS, ticket.structured?.frequency);
                  if (issueLabel) metaChips.push(`Type: ${issueLabel}`);
                  if (impactLabel) metaChips.push(`Impact: ${impactLabel}`);
                  if (frequencyLabel) metaChips.push(`Frequency: ${frequencyLabel}`);
                  if (typeof ticket.structured?.isBlocking === 'boolean') {
 metaChips.push(ticket.structured.isBlocking ? 'Blocking' : 'Not blocking');
                  }

                  const environment = ticket.structured?.environment || {};
                  const envParts = [];
                  if (environment.device) envParts.push(environment.device);
                  if (environment.operatingSystem) envParts.push(environment.operatingSystem);
                  if (environment.browser) envParts.push(environment.browser);
                  if (environment.connection) envParts.push(environment.connection);
                  const environmentLabel = envParts.join(' - ');

                  const alreadyTried = ticket.structured?.selfHelpTried || [];
                  const status = ticket.status || 'open';

                  return (
                    <Card key={ticket._id || ticket.createdAt} variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Stack spacing={1.5}>
                          <Stack direction="row" justifyContent="space-between" spacing={2}>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ color: TEXT_COLOR }}>
                              {ticket.subject}
                            </Typography>
                            <Typography variant="caption" sx={{ color: TEXT_COLOR }}>
                              {formatDateTime(ticket.createdAt)}
                            </Typography>
                          </Stack>

                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Chip
 label={status === 'resolved' ? 'Resolved' : status.charAt(0).toUpperCase() + status.slice(1)}
 color={status === 'resolved' ? 'success' : 'default'}
                              size="small"
                            />
                            {metaChips.map((chip) => (
                              <Chip key={chip} label={chip} variant="outlined" size="small" />
                            ))}
                            {ticket.structured?.featureArea && (
                              <Chip
                                label={`Area: ${ticket.structured.featureArea}`}
                                variant="outlined"
                                size="small"
                              />
                            )}
                            {environmentLabel && (
                              <Chip label={`Environment: ${environmentLabel}`} variant="outlined" size="small" />
                            )}
                          </Stack>

                          <Box>
                            <Typography variant="subtitle2" sx={{ color: TEXT_COLOR }}>
                              Message
                            </Typography>
                            <Typography variant="body2" sx={{ color: TEXT_COLOR, whiteSpace: 'pre-wrap' }}>
                              {ticket.message}
                            </Typography>
                          </Box>

                          {alreadyTried.length > 0 && (
                            <Typography variant="body2" sx={{ color: TEXT_COLOR }}>
                              <strong>Already tried:</strong> {alreadyTried.join(', ')}
                            </Typography>
                          )}

                          {ticket.responses && ticket.responses.length > 0 && (
                            <Box>
                              <Typography variant="subtitle2" sx={{ color: TEXT_COLOR, mb: 1 }}>
                                Responses
                              </Typography>
                              <Stack spacing={1}>
                                {ticket.responses.map((response, index) => (
                                  <Box
                                    key={index}
                                    sx={{
                                      borderRadius: 1,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      bgcolor: 'grey.50',
                                      p: 1.5,
                                    }}
                                  >
                                    <Typography variant="caption" sx={{ color: TEXT_COLOR, fontWeight: 600 }}>
                                      {formatDateTime(response.at)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: TEXT_COLOR }}>
                                      {response.message}
                                    </Typography>
                                  </Box>
                                ))}
                              </Stack>
                            </Box>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            )}
          </CardContent>
        </Card>

        <SupportResourcesPanel userRole={userRole} />
      </Stack>
    </Box>
  );
};

export default SupportTicketForm;
